import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  from,
  throwError,
  of as rxOf,
} from 'rxjs';
import { catchError, filter, switchMap, take, map } from 'rxjs/operators';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Router } from '@angular/router';
import { CognitoService } from '../service/cognito.service';
import { AuthStorageService } from './auth-storage.service';

@Injectable({
  providedIn: 'root',
})
export class RefreshTokenService {
  private isRefreshing: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  private router = inject(Router);
  private maxRetries = 3;
  private retryCount = 0;

  constructor(
    private cognitoService: CognitoService,
    private authStorage: AuthStorageService
  ) {}

  refreshToken(): Observable<string> {
    if (!this.isRefreshing.value) {
      this.isRefreshing.next(true);
      this.refreshTokenSubject.next(null);

      const refreshObservable = from(
        fetchAuthSession({ forceRefresh: true }).then((session) => {
          const token = session.tokens?.accessToken?.toString();
          if (!token) {
            throw new Error('No access token received from refresh');
          }
          return token;
        })
      );

      return refreshObservable.pipe(
        switchMap((newToken: string) => {
          this.isRefreshing.next(false);
          this.retryCount = 0;

          if (!newToken) {
            throw new Error('No token received');
          }

          this.authStorage.setItem('accessToken', newToken);
          this.refreshTokenSubject.next(newToken);
          console.log('Token refreshed successfully');
          return rxOf(newToken);
        }),
        catchError((error) => {
          this.isRefreshing.next(false);
          console.error(
            `Token refresh failed (attempt ${this.retryCount + 1}/${
              this.maxRetries
            }):`,
            error
          );

          this.retryCount++;

          if (this.retryCount < this.maxRetries) {
            console.log(`Retrying token refresh in 1 second...`);
            return new Observable<string>((subscriber) => {
              setTimeout(() => {
                this.refreshToken().subscribe({
                  next: (token) => subscriber.next(token),
                  error: (err) => subscriber.error(err),
                  complete: () => subscriber.complete(),
                });
              }, 1000);
            });
          }

          console.log(
            'Max token refresh retries exceeded, signing out user...'
          );
          this.refreshTokenSubject.next(null);
          this.retryCount = 0;

          this.authStorage.clearAuthData();

          this.cognitoService.signOutAndRedirect().subscribe();
          return throwError(
            () => new Error('Token refresh failed after maximum retries')
          );
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        map((token) => token as string)
      );
    }
  }

  public get refreshTokenInProgress(): Observable<boolean> {
    return this.isRefreshing.asObservable();
  }
}
